import type { Node } from 'prosemirror-model';
import { type IntlShape } from 'react-intl';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import uuid from 'uuid';

import { type EditorState } from '@atlaskit/editor-prosemirror/state';
import { sectionCtaMessages } from '@atlassian/editor-plugin-company-hub-common/messages';

import {
	DEFAULT_BACKGROUND_COLOR,
	DEFAULT_CONTENT_WIDTH,
	HUB_ELEMENT_PLACEHOLDER_EXTENSION_KEY,
	HUB_ELEMENT_PLACEHOLDER_EXTENSION_TYPE,
	sectionExtensionKey,
	sectionExtensionType,
} from './constants';

// Section content structure
export const getSectionExtensionContents = ({
	header,
	subHeader,
	placeholderTitle,
}: {
	header: string;
	placeholderTitle: string;
	subHeader: string;
}): {
	content: (
		| {
				attrs: {
					extensionKey?: undefined;
					extensionType?: undefined;
					layout?: undefined;
					level: number;
					localId: null;
					parameters?: undefined;
					text?: undefined;
				};
				content: {
					text: string;
					type: string;
				}[];
				type: string;
		  }
		| {
				attrs: {
					extensionKey?: undefined;
					extensionType?: undefined;
					layout?: undefined;
					level?: undefined;
					localId: null;
					parameters?: undefined;
					text?: undefined;
				};
				content: {
					text: string;
					type: string;
				}[];
				type: string;
		  }
		| {
				attrs: {
					extensionKey?: undefined;
					extensionType?: undefined;
					layout?: undefined;
					level?: undefined;
					localId: null;
					parameters?: undefined;
					text?: undefined;
				};
				content?: undefined;
				type: string;
		  }
		| {
				attrs: {
					extensionKey: string;
					extensionType: string;
					layout: string;
					level?: undefined;
					localId: null;
					parameters: {
						extensionTitle: string;
					};
					text: null;
				};
				content?: undefined;
				type: string;
		  }
	)[];
	type: string;
} => ({
	type: 'extensionFrame',
	content: [
		{
			type: 'heading',
			attrs: {
				level: 1,
				localId: null,
			},
			content: [
				{
					type: 'text',
					text: `${header}`,
				},
			],
		},
		{
			type: 'paragraph',
			attrs: {
				localId: null,
			},
			content: [
				{
					type: 'text',
					text: `${subHeader}`,
				},
			],
		},
		{
			type: 'paragraph',
			attrs: {
				localId: null,
			},
		},
		{
			type: 'extension',
			attrs: {
				extensionKey: HUB_ELEMENT_PLACEHOLDER_EXTENSION_KEY,
				extensionType: HUB_ELEMENT_PLACEHOLDER_EXTENSION_TYPE,
				parameters: { extensionTitle: placeholderTitle },
				text: null,
				layout: 'default',
				localId: null,
			},
		},
	],
});

const sectionContentNode = (
	state: EditorState,
	initialContentText: {
		header: string;
		placeholderTitle: string;
		subHeader: string;
	},
) => state.schema.nodeFromJSON(getSectionExtensionContents(initialContentText));

export const newSectionNode = (state: EditorState, intl: IntlShape): Node => {
	const initialContentText = {
		header: intl.formatMessage(sectionCtaMessages.contentHeader),
		subHeader: intl.formatMessage(sectionCtaMessages.contentSubHeader),
		placeholderTitle: intl.formatMessage(sectionCtaMessages.placeHolderTitle),
	};
	return state.schema.nodes.multiBodiedExtension.create(
		{
			extensionType: sectionExtensionType,
			extensionKey: sectionExtensionKey,
			layout: 'full-width',
			// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
			localId: uuid(),
			parameters: {
				extensionTitle: intl.formatMessage(sectionCtaMessages.lozengeTitle),
				backgroundColor: DEFAULT_BACKGROUND_COLOR,
				contentWidth: DEFAULT_CONTENT_WIDTH,
			},
		},
		sectionContentNode(state, initialContentText),
	);
};
